<script setup lang="ts">
  import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref, watch } from "vue";
  import { storeToRefs } from "pinia";
  import AssistantPanel from "./AssistantPanel.vue";
  import AssistantThread from "./AssistantThread.vue";
  import { useAssistantPageRefresh } from "./useAssistantPageRefresh";
  import { useAssistantStore, type AssistantAnswer } from "@/stores/assistant";
  import { useCurrentUserStore } from "@/stores/currentUser";
  import { useRoute } from "vue-router";
  import { countActiveFilters, useAssistantScope } from "@/composables/useAssistantScope";

  const store = useAssistantStore();
  const {
    open,
    conversations,
    currentConversationToken,
    currentTitle,
    messages,
    isStreaming,
    streamingContent,
    streamingSteps,
    streamingSummary,
    scopeDefinition,
    suggestionGroups,
    lastTurn,
    queuedPrompt,
    mutatedAt,
    mutatedTools,
  } = storeToRefs(store);
  const { getUser } = storeToRefs(useCurrentUserStore());

  const { scope } = useAssistantScope();
  const { refreshAfterWrite } = useAssistantPageRefresh();
  const instance = getCurrentInstance();
  const thread = ref<InstanceType<typeof AssistantThread> | null>(null);
  const route = useRoute();

  // On the Episto AI page the thread is the page itself.
  const onEpistoAiPage = computed(() => route.name === "EpistoAi");

  store.loadScopeDefinitions();

  // The scope is what the assistant is bound to, so it is also what identifies
  // a conversation. Leaving the page — a different scope or a different
  // record — closes the panel and starts a fresh thread; a filter change or a
  // move between a dashboard and one of its views keeps it.
  watch(
    () => (scope.value ? `${scope.value.key}:${scope.value.contextToken ?? ""}` : null),
    () => store.bindScope(scope.value),
    { immediate: true },
  );

  // The assistant wrote something: bring the page it changed up to date.
  watch(mutatedAt, () => refreshAfterWrite(mutatedTools.value, surveyToken()));

  // A page asked to open the panel on a prompt of its own.
  watch(queuedPrompt, (prompt) => {
    if (!prompt) return;

    sendMessage(store.takeQueuedPrompt() as string);
  });

  const scopeHint = computed(() => {
    if (!scopeDefinition.value) return null;

    const filters = countActiveFilters(scope.value?.pageFilters ?? {});
    return { verb: scopeDefinition.value.verb, target: scopeDefinition.value.target, filters };
  });

  // The survey a write may have invalidated. On a report the panel carries the
  // report token, and the survey store already holds the survey being viewed.
  function surveyToken() {
    if (scope.value?.contextType === "survey") return scope.value.contextToken;
    return null;
  }

  function startConversation() {
    store.newConversation();
    thread.value?.focusComposer();
  }

  async function selectConversation(token: string) {
    await store.openConversation(token);
    thread.value?.focusComposer();
  }

  function sendMessage(content: string, files: File[] = []) {
    store.sendMessage(content, instance?.proxy?.$cable, instance, files, scope.value);
  }

  function answer(messageId: string, text: string, answers: Record<string, AssistantAnswer>) {
    store.answerQuestion(messageId, text, answers, instance?.proxy?.$cable, instance);
  }

  function onKeydown(event: KeyboardEvent) {
    // Chrome binds ⌘J to its Downloads page, so the default has to go.
    if (event.key?.toLowerCase() !== "j" || !(event.metaKey || event.ctrlKey)) return;
    if (!scope.value || onEpistoAiPage.value) return;

    event.preventDefault();
    store.toggle();
  }

  onMounted(() => window.addEventListener("keydown", onKeydown));
  onBeforeUnmount(() => window.removeEventListener("keydown", onKeydown));
</script>

<template>
  <AssistantPanel
    :open="open && !!scope && !onEpistoAiPage"
    :working="isStreaming"
    :conversation-title="currentTitle"
    :conversations="conversations"
    :current-conversation-token="currentConversationToken"
    @close="store.toggle(false)"
    @new="startConversation"
    @select-conversation="selectConversation">
    <AssistantThread
      ref="thread"
      :messages="messages"
      :is-streaming="isStreaming"
      :streaming-content="streamingContent"
      :streaming-steps="streamingSteps"
      :streaming-summary="streamingSummary"
      :suggestion-groups="suggestionGroups"
      :scope-hint="scopeHint"
      :first-name="getUser?.first_name ?? ''"
      :can-retry="!!lastTurn"
      :survey-token="surveyToken()"
      @send="sendMessage"
      @stop="store.stopGeneration"
      @retry="store.retryLastTurn"
      @feedback="store.sendFeedback"
      @answer="answer" />
  </AssistantPanel>
</template>
